/**
 * Fretboard — visual guitar neck that is both a display and a tap-input surface.
 *
 * Fully controlled: the parent owns selection state, decides when to submit,
 * grades, and feeds results back via `litPositions`. The board only draws what
 * it's told and reports taps (mirrors how DictationInput reports a sequence and
 * the route grades it). The geometry and keyboard-navigation rules live in the
 * pure `fretboard-layout` module.
 *
 * Accessibility: an ARIA grid with a roving tabindex — the board is one tab
 * stop; arrow keys move focus across strings/frets, Enter/Space taps.
 *
 * See docs/design/guitar-harmony-v1.md §7.
 */

import { useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import {
  type Tuning,
  type FretPosition,
  midiAt,
  pitchFromMidi,
  pitchToString,
} from "@music-app/theory-engine";
import {
  type FretWindow,
  type FretNavKey,
  cellCenter,
  fretCenterX,
  fretColumnWidth,
  fretWireXs,
  nutX,
  fretsInWindow,
  fullWindow,
  stringCount,
  nextFocus,
  initialFocus,
} from "./fretboard-layout.js";

export type FretRole = "given" | "selected" | "correct" | "duplicate" | "wrong" | "solution";

export interface LitPosition {
  pos: FretPosition;
  role: FretRole;
  /** Overrides the `noteLabel`-derived dot text (e.g. a solfège syllable the
   *  parent computed from the key context). */
  label?: string;
}

export interface FretboardProps {
  tuning: Tuning;
  /** Frets to render. Defaults to the full neck. */
  fretWindow?: FretWindow;
  /** Everything currently lit; role drives colour + ARIA state. */
  litPositions?: LitPosition[];
  /** Strings the user may tap. Others render dimmed + non-interactive. */
  allowedStrings?: number[];
  /** Fired on tap / Enter / Space. Omitted ⇒ display-only (role="img"). */
  onTap?: (pos: FretPosition) => void;
  /** Freezes interaction while a graded result is shown. */
  disabled?: boolean;
  /** Required — the board's accessible name. */
  ariaLabel: string;
  /** Dot text. "letter" derives the note name; "solfege" relies on per-position
   *  `label` overrides (the board has no key context); "none" shows blank dots. */
  noteLabel?: "solfege" | "letter" | "none";
}

const INLAY_FRETS = new Set([3, 5, 7, 9]);
const DOUBLE_INLAY_FRET = 12;

const VIEW_W = 1000;
const INSET = { left: 52, right: 24, top: 22, bottom: 22 };

const ROLE_DOT: Record<FretRole, string> = {
  given: "fill-inkFaded",
  selected: "fill-accent",
  correct: "fill-success",
  duplicate: "fill-warning",
  wrong: "fill-danger",
  solution: "fill-none stroke-success",
};

export function Fretboard({
  tuning,
  fretWindow,
  litPositions = [],
  allowedStrings,
  onTap,
  disabled = false,
  ariaLabel,
  noteLabel = "solfege",
}: FretboardProps): ReactNode {
  const window = fretWindow ?? fullWindow(tuning);
  const strings = stringCount(tuning);
  const frets = fretsInWindow(window);
  const interactive = !!onTap && !disabled;
  const allowed = useMemo(
    () => new Set(allowedStrings ?? Array.from({ length: strings }, (_, i) => i)),
    [allowedStrings, strings],
  );

  const [focus, setFocus] = useState<FretPosition>(() =>
    initialFocus(tuning, window, allowedStrings),
  );
  const cellRefs = useRef(new Map<string, SVGRectElement | null>());
  const keyOf = (p: FretPosition) => `${p.stringIndex}:${p.fret}`;

  const viewH = INSET.top + INSET.bottom + (strings - 1) * 30;
  const px = (xNorm: number) => INSET.left + xNorm * (VIEW_W - INSET.left - INSET.right);
  const py = (yNorm: number) => INSET.top + yNorm * (viewH - INSET.top - INSET.bottom);
  const center = (pos: FretPosition) => {
    const c = cellCenter(tuning, pos, window);
    return { cx: px(c.x), cy: py(c.y) };
  };
  const innerW = VIEW_W - INSET.left - INSET.right;
  const colWidth = fretColumnWidth(window) * innerW;
  const dotRadius = Math.min(15, colWidth * 0.42);

  const stringDisplayNumber = (s: number) => strings - s; // 0 (low E) → 6
  const noteNameAt = (pos: FretPosition) => pitchToString(pitchFromMidi(midiAt(tuning, pos)));
  const letterAt = (pos: FretPosition) => noteNameAt(pos).replace(/-?\d+$/, "");

  const cellAriaLabel = (pos: FretPosition) => {
    const open = pitchToString(tuning.openStrings[pos.stringIndex]!).replace(/-?\d+$/, "");
    return `string ${stringDisplayNumber(pos.stringIndex)} (${open}), fret ${pos.fret}, ${letterAt(pos)}`;
  };

  const dotText = (lit: LitPosition): string => {
    if (lit.label !== undefined) return lit.label;
    if (noteLabel === "letter") return letterAt(lit.pos);
    return "";
  };

  function focusCell(pos: FretPosition) {
    setFocus(pos);
    cellRefs.current.get(keyOf(pos))?.focus();
  }

  function onGridKeyDown(e: KeyboardEvent<SVGSVGElement>) {
    if (!interactive) return;
    const navKeys: FretNavKey[] = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"];
    if (navKeys.includes(e.key as FretNavKey)) {
      e.preventDefault();
      focusCell(nextFocus(focus, e.key as FretNavKey, { tuning, window, allowedStrings }));
      return;
    }
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (allowed.has(focus.stringIndex)) onTap!(focus);
    }
  }

  const litSummary = litPositions.map((l) => `${letterAt(l.pos)} (${l.role})`).join(", ");

  return (
    <div className="w-full" style={{ aspectRatio: `${VIEW_W} / ${viewH}` }}>
      <svg
        className="w-full h-full select-none"
        viewBox={`0 0 ${VIEW_W} ${viewH}`}
        role={interactive ? "grid" : "img"}
        aria-label={interactive ? ariaLabel : `${ariaLabel}. ${litSummary}`}
        onKeyDown={onGridKeyDown}
      >
        {/* board surface */}
        <rect x={INSET.left} y={INSET.top} width={VIEW_W - INSET.left - INSET.right}
          height={viewH - INSET.top - INSET.bottom} rx={4} className="fill-paperDeep" />

        {/* inlays (in the fret space, matching the note positions) */}
        {frets.map((f) => {
          if (f === DOUBLE_INLAY_FRET) {
            const c = px(fretCenterX(f, window));
            return (
              <g key={`inlay-${f}`} className="fill-ink/20" aria-hidden>
                <circle cx={c} cy={py(0.25)} r={5} />
                <circle cx={c} cy={py(0.75)} r={5} />
              </g>
            );
          }
          if (INLAY_FRETS.has(f)) {
            return (
              <circle key={`inlay-${f}`} aria-hidden cx={px(fretCenterX(f, window))}
                cy={py(0.5)} r={5} className="fill-ink/20" />
            );
          }
          return null;
        })}

        {/* fret wires + nut (the nut is thicker; open dots sit in the gutter left of it) */}
        {window.from === 0 && (
          <line aria-hidden x1={px(nutX(window))} y1={INSET.top} x2={px(nutX(window))} y2={viewH - INSET.bottom}
            className="stroke-ink/50" strokeWidth={4} />
        )}
        {fretWireXs(window).map((xn, k) => {
          const x = px(xn);
          return (
            <line key={`wire-${k}`} aria-hidden x1={x} y1={INSET.top} x2={x} y2={viewH - INSET.bottom}
              className="stroke-ink/40" strokeWidth={1} />
          );
        })}

        {/* strings (string 0 / low E at the bottom) */}
        {Array.from({ length: strings }, (_, s) => {
          const y = py(strings <= 1 ? 0.5 : 1 - s / (strings - 1));
          const dimmed = !allowed.has(s);
          return (
            <line key={`string-${s}`} aria-hidden x1={INSET.left} y1={y} x2={VIEW_W - INSET.right} y2={y}
              className={dimmed ? "stroke-ink/15" : "stroke-ink/60"}
              strokeWidth={1 + (strings - 1 - s) * 0.35} />
          );
        })}

        {/* lit dots */}
        {litPositions.map((lit, i) => {
          const { cx, cy } = center(lit.pos);
          const outline = lit.role === "solution";
          return (
            <g key={`lit-${i}`} aria-hidden style={{ pointerEvents: "none" }}>
              <circle cx={cx} cy={cy} r={dotRadius}
                className={ROLE_DOT[lit.role]} strokeWidth={outline ? 2.5 : 0} />
              {dotText(lit) && (
                <text x={cx} y={cy + dotRadius * 0.34} textAnchor="middle"
                  style={{ fontSize: `${Math.round(dotRadius * 0.92)}px` }}
                  className={outline ? "fill-success" : "fill-paper"}>
                  {dotText(lit)}
                </text>
              )}
            </g>
          );
        })}

        {/* interactive hit cells — ARIA grid, one row per string */}
        {interactive &&
          Array.from({ length: strings }, (_, s) => (
            <g key={`row-${s}`} role="row">
              {frets.map((f) => {
                const pos: FretPosition = { stringIndex: s, fret: f };
                const isAllowed = allowed.has(s);
                const isFocus = focus.stringIndex === s && focus.fret === f;
                const selected = litPositions.some(
                  (l) => l.role === "selected" && l.pos.stringIndex === s && l.pos.fret === f,
                );
                const { cx, cy } = center(pos);
                return (
                  <rect
                    key={`cell-${s}-${f}`}
                    ref={(el) => cellRefs.current.set(keyOf(pos), el)}
                    role="gridcell"
                    aria-label={cellAriaLabel(pos)}
                    aria-selected={selected}
                    aria-disabled={!isAllowed}
                    tabIndex={isAllowed && isFocus ? 0 : -1}
                    x={cx - colWidth / 2}
                    y={cy - 15}
                    width={colWidth}
                    height={30}
                    className="fill-transparent focus:outline-none focus-visible:fill-accent/15"
                    style={{ cursor: isAllowed ? "pointer" : "default" }}
                    onFocus={() => setFocus(pos)}
                    onClick={isAllowed ? () => onTap!(pos) : undefined}
                  />
                );
              })}
            </g>
          ))}
      </svg>
    </div>
  );
}
